import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { semantic_colors, text_styles } from '../../foundation';
import { IconPictureX } from '../icons/generated';
import { useProviderConfig } from '../provider';
import { buildCSSWithLength, CSSValueWithLength } from '../../styles';

export interface ImagePreviewProps {
  src?: string;
  /** @default 100 */
  width?: CSSValueWithLength;
  /** @default 100 */
  height?: CSSValueWithLength;
  /** @default 8 */
  borderRadius?: CSSValueWithLength;
  alt?: string;
  label?: string;
  objectFit: 'contain' | 'cover' | 'fill';
  /** @default 'medium' */
  size?: 'medium' | 'small';
  backgroundDark?: boolean;
  error?: boolean;
  onClick?: (image_url: string) => void;
}

export const getImagePreviewSize = ({
  size,
  width = 100,
  height = 100,
}: Pick<ImagePreviewProps, 'size' | 'width' | 'height'>) => {
  if (size === 'small') {
    return `
      ${buildCSSWithLength('width', 40)}
      ${buildCSSWithLength('height', 40)}
    `;
  }
  return `
    ${buildCSSWithLength('width', width)}
    ${buildCSSWithLength('height', height)}
  `;
};

export const ImagePreview = React.forwardRef<HTMLImageElement, ImagePreviewProps>(
  (
    {
      size = 'medium',
      width = 100,
      height = 100,
      borderRadius = 8,
      objectFit,
      src,
      alt,
      label,
      backgroundDark,
      error,
      onClick,
    },
    ref,
  ) => {
    const { locale } = useProviderConfig();
    const [onload_error, setOnloadError] = React.useState<boolean>(false);

    useEffect(() => {
      setOnloadError(!src);
    }, [src]);

    return (
      <ImagePreviewContainer
        size={size}
        width={width}
        height={height}
        borderRadius={borderRadius}
        className='pds-image-preview-container'
      >
        <UploadPreviewBg
          backgroundDark={backgroundDark}
          error={error}
          onload_error={onload_error}
          borderRadius={borderRadius}
        >
          {error || onload_error ? (
            <IconPictureX
              role='img'
              aria-label={locale?.ImageUpload.fail}
              size={24}
              color={error ? semantic_colors.state.negative : semantic_colors.content.tertiary}
            />
          ) : (
            <Image
              className='pds-image-preview'
              role='img'
              ref={ref}
              src={src}
              alt={alt || ''}
              objectFit={objectFit}
              backgroundDark={backgroundDark}
              onClick={() => onClick?.(src ?? '')}
              has_click={!!onClick}
              onError={() => setOnloadError(true)}
              onLoad={() => setOnloadError(false)}
            />
          )}
        </UploadPreviewBg>
        {size === 'medium' && label && <Label>{label}</Label>}
      </ImagePreviewContainer>
    );
  },
);

export const ImagePreviewContainer = styled.div<Pick<ImagePreviewProps, 'size' | 'width' | 'height' | 'borderRadius'>>`
  position: relative;
  ${({ borderRadius }) => buildCSSWithLength('border-radius', borderRadius)}
  ${({ size, width, height }) => getImagePreviewSize({ size, width, height })};
  overflow: hidden;
`;

const UploadPreviewBg = styled.div<{
  error?: boolean;
  backgroundDark?: boolean;
  onload_error?: boolean;
  borderRadius?: CSSValueWithLength;
}>`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  ${({ borderRadius }) => buildCSSWithLength('border-radius', borderRadius)}
  background-color: ${({ backgroundDark }) =>
    backgroundDark ? semantic_colors.background.scrim : semantic_colors.background.surface};
  color: ${semantic_colors.content.primary};
  border: ${({ backgroundDark }) => (backgroundDark ? 'initial' : `1px solid ${semantic_colors.border.primary}`)};

  ${({ error }) =>
    error &&
    `
    background-color: ${semantic_colors.state.negative_light};
    border: 1px solid ${semantic_colors.state.negative};
    color: ${semantic_colors.state.negative};
  `}
  ${({ onload_error }) =>
    onload_error &&
    `
    background-color: ${semantic_colors.background.base};
    border: 1px solid ${semantic_colors.border.primary};
    color: ${semantic_colors.content.tertiary};
  `}
`;

const Image = styled.img<{
  objectFit: 'contain' | 'cover' | 'fill';
  backgroundDark?: boolean;
  has_click?: boolean;
}>`
  position: absolute;
  object-fit: ${({ objectFit }) => objectFit};
  ${({ backgroundDark }) => !backgroundDark && `mix-blend-mode: multiply;`}
  width: 100%;
  height: 100%;
  ${({ has_click }) => has_click && 'cursor: pointer;'}
  background-image: linear-gradient(45deg, ${semantic_colors.fill
    .secondary} 25%, transparent 25%), linear-gradient(-45deg, ${semantic_colors.fill
    .secondary} 25%, transparent 25%), linear-gradient(45deg, transparent 75%, ${semantic_colors.fill
    .secondary} 75%), linear-gradient(-45deg, transparent 75%, ${semantic_colors.fill.secondary} 75%);
  background-size: 20px 20px;
  background-position: 0 0, 0 10px, 10px -10px, -10px 0;
`;

const Label = styled.span`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  text-align: center;
  padding: 2px 0;
  ${text_styles.Body_13_Medium};
  color: ${semantic_colors.content.primary_inverse};
  background-color: ${semantic_colors.background.scrim};
`;
